import { useEffect, useRef, useState } from "react";
import { Dashboard } from "./components/Dashboard";
import { Intro } from "./components/Intro";
import { LoginScreen } from "./components/LoginScreen";
import { ADMIN_LOGIN, ADMIN_PASSWORD, authenticateUser, createInitialAppState } from "./data/appModel";
import { cloudDiagnostics, supabase } from "./lib/supabase";
import type { AppState, NavKey, Role, SessionUser, Vehicle } from "./types";

const STORAGE_KEY = "sng-dispatch-state-v1";

function normalizeText(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ");
}

function parseActiveFlag(value: unknown, fallback = true) {
  const normalized = normalizeText(value);
  if (!normalized) return fallback;
  if (["nie", "nieaktywny", "nieaktywna", "dezaktywowany", "dezaktywowana", "false", "0", "inactive"].includes(normalized)) return false;
  if (["tak", "aktywny", "aktywna", "true", "1", "active"].includes(normalized)) return true;
  return fallback;
}

const seedUserLogins = new Set(["dyspozytor"]);
const seedCarrierIds = new Set(["car-sng", "car-north", "car-baltic"]);
const seedDriverIds = new Set(["drv-001", "drv-002", "drv-003", "drv-004"]);
const seedVehicleIds = new Set(["veh-001", "veh-002", "veh-003", "veh-004"]);
const seedFolderIds = new Set(["cdf-general-sng"]);
const seedRouteIds = new Set(["rou-001", "rou-002", "rou-003", "rou-004"]);
const seedScheduleIds = new Set(["sch-001", "sch-002", "sch-003", "sch-004", "sch-005", "sch-006"]);
const seedRequestIds = new Set(["REQ-1001", "REQ-1002"]);
const seedNotificationIds = new Set(["not-001", "not-002"]);
const seedArchiveIds = new Set(["arc-001", "arc-002"]);
const seedTaskIds = new Set(["task-001", "task-002"]);

function stripSeedData(state: AppState): AppState {
  const users = state.users.filter((account) => !seedUserLogins.has(normalizeText(account.login)));
  const carriers = state.carriers.filter((carrier) => !seedCarrierIds.has(carrier.id));
  const drivers = state.drivers.filter((driver) => !seedDriverIds.has(driver.id) && !seedCarrierIds.has(driver.carrierId));
  const driverIds = new Set(drivers.map((driver) => driver.id));
  const carrierIds = new Set(carriers.map((carrier) => carrier.id));

  return {
    ...state,
    users: users.length ? users : createInitialAppState().users,
    carriers,
    drivers,
    vehicles: state.vehicles.filter((vehicle) => !seedVehicleIds.has(vehicle.id) && !seedCarrierIds.has(vehicle.carrierId)),
    carrierDocuments: state.carrierDocuments.filter((document) => !seedCarrierIds.has(document.carrierId)),
    carrierDocumentFolders: state.carrierDocumentFolders.filter((folder) => !seedFolderIds.has(folder.id) && !seedCarrierIds.has(folder.carrierId)),
    routes: state.routes.filter((route) => !seedRouteIds.has(route.id) && (!route.driverId || driverIds.has(route.driverId))),
    schedule: state.schedule.filter((entry) =>
      !seedScheduleIds.has(entry.id) &&
      !seedDriverIds.has(entry.driverId ?? "") &&
      !seedCarrierIds.has(entry.carrierId) &&
      (!entry.driverId || driverIds.has(entry.driverId)) &&
      (!entry.carrierId || carrierIds.has(entry.carrierId))
    ),
    requests: state.requests.filter((request) => !seedRequestIds.has(request.id)),
    notifications: state.notifications.filter((notification) => !seedNotificationIds.has(notification.id)),
    archive: state.archive.filter((item) => !seedArchiveIds.has(item.id)),
    tasks: state.tasks.filter((task) => !seedTaskIds.has(task.id))
  };
}

function mergeAppState(parsed: Partial<AppState> | null | undefined): AppState {
  const fallback = createInitialAppState();

  if (!parsed) {
    return stripSeedData(fallback);
  }

  const sourceDrivers = parsed.drivers?.length ? parsed.drivers : fallback.drivers;
  const sourceVehicles = (parsed as Partial<AppState> & { vehicles?: Vehicle[] }).vehicles?.length
    ? (parsed as Partial<AppState> & { vehicles?: Vehicle[] }).vehicles ?? []
    : fallback.vehicles;

  const merged = {
    ...fallback,
    ...parsed,
    users: parsed.users?.length ? parsed.users : fallback.users,
    carriers: parsed.carriers?.length ? parsed.carriers : fallback.carriers,
    drivers: sourceDrivers.map((driver) => ({ ...driver, active: parseActiveFlag(driver.active, true) })),
    vehicles: sourceVehicles.map((vehicle) => ({ ...vehicle, active: parseActiveFlag(vehicle.active, true) })),
    routes: parsed.routes?.length ? parsed.routes : fallback.routes,
    schedule: parsed.schedule ?? fallback.schedule,
    carrierDocuments: parsed.carrierDocuments ?? fallback.carrierDocuments,
    carrierDocumentFolders: parsed.carrierDocumentFolders ?? fallback.carrierDocumentFolders,
    requests: parsed.requests ?? fallback.requests,
    notifications: parsed.notifications ?? fallback.notifications,
    archive: parsed.archive ?? fallback.archive,
    routeTypes: parsed.routeTypes?.length ? parsed.routeTypes : fallback.routeTypes,
    pointTypes: parsed.pointTypes?.length ? parsed.pointTypes : fallback.pointTypes,
    customRoles: parsed.customRoles ?? fallback.customRoles,
    tasks: parsed.tasks ?? fallback.tasks,
    notes: parsed.notes ?? fallback.notes
  } as AppState;

  return stripSeedData(merged);
}

function loadAppState(): AppState {
  const fallback = createInitialAppState();

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return fallback;
    }

    const parsed = JSON.parse(raw) as Partial<AppState>;
    return mergeAppState(parsed);
  } catch {
    return fallback;
  }
}

function nowStamp() {
  return new Date().toLocaleString("pl-PL");
}

function createId(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function App() {
  const params = new URLSearchParams(window.location.search);
  const [appState, setAppState] = useState<AppState>(loadAppState);
  const appStateRef = useRef(appState);
  const applyingCloudStateRef = useRef(false);
  const lastCloudUpdatedAtRef = useRef<string | null>(null);
  const lastSavedCloudStateRef = useRef("");
  const [cloudLoading, setCloudLoading] = useState(Boolean(supabase));
  const [introVisible, setIntroVisible] = useState(() => {
    return !params.has("skipIntro");
  });
  const [user, setUser] = useState<SessionUser | null>(() => {
    if (!params.has("e2eAdmin")) {
      return null;
    }

    return {
      id: "usr-admin",
      login: ADMIN_LOGIN,
      name: ADMIN_LOGIN,
      role: "administrator"
    };
  });
  const [initialView, setInitialView] = useState<NavKey>("pulpit");
  const [cloudReady, setCloudReady] = useState(!supabase);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(appState));

    const client = supabase;
    if (!client || !cloudReady) {
      return;
    }
    const cloudClient = client;

    if (applyingCloudStateRef.current) {
      applyingCloudStateRef.current = false;
      return;
    }

    const serializedState = JSON.stringify(appState);
    if (serializedState === lastSavedCloudStateRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      void (async () => {
        const { data, error } = await cloudClient
          .from("sng_app_state")
          .upsert(
            {
              id: "main",
              state: appState
            },
            { onConflict: "id" }
          )
          .select("updated_at")
          .single();

        if (error) {
          console.error("SNG cloud save error", error);
          return;
        }

        lastSavedCloudStateRef.current = serializedState;
        lastCloudUpdatedAtRef.current = data?.updated_at ?? lastCloudUpdatedAtRef.current;
      })();
    }, 500);

    return () => window.clearTimeout(timer);
  }, [appState, cloudReady]);

  useEffect(() => {
    const client = supabase;
    if (!client) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const { data, error } = await client
          .from("sng_app_state")
          .select("state, updated_at")
          .eq("id", "main")
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          console.error("SNG cloud load error", error);
          setCloudReady(false);
          setCloudLoading(false);
          return;
        }
        if (data?.state) {
          const cloudState = mergeAppState(data.state as Partial<AppState>);
          applyingCloudStateRef.current = true;
          lastSavedCloudStateRef.current = JSON.stringify(cloudState);
          lastCloudUpdatedAtRef.current = data.updated_at ?? null;
          setAppState(cloudState);
        }
        setCloudReady(true);
        setCloudLoading(false);
      } catch {
        if (!cancelled) {
          setCloudReady(false);
          setCloudLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const client = supabase;
    if (!client || !cloudReady) {
      return;
    }
    const cloudClient = client;

    let cancelled = false;

    async function refreshFromCloud() {
      const { data, error } = await cloudClient
        .from("sng_app_state")
        .select("state, updated_at")
        .eq("id", "main")
        .maybeSingle();

      if (cancelled || error || !data?.state) {
        if (error) {
          console.error("SNG cloud refresh error", error);
        }
        return;
      }

      if (!data.updated_at || data.updated_at === lastCloudUpdatedAtRef.current) {
        return;
      }

      const cloudState = mergeAppState(data.state as Partial<AppState>);
      const serializedCloudState = JSON.stringify(cloudState);
      if (serializedCloudState === JSON.stringify(appStateRef.current)) {
        lastCloudUpdatedAtRef.current = data.updated_at;
        lastSavedCloudStateRef.current = serializedCloudState;
        return;
      }

      applyingCloudStateRef.current = true;
      lastCloudUpdatedAtRef.current = data.updated_at;
      lastSavedCloudStateRef.current = serializedCloudState;
      setAppState(cloudState);
    }

    const interval = window.setInterval(refreshFromCloud, 1000);
    window.addEventListener("focus", refreshFromCloud);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshFromCloud);
    };
  }, [cloudReady]);

  useEffect(() => {
    setAppState((current) => {
      const hasAdmin = current.users.some((account) => account.login.toLowerCase() === ADMIN_LOGIN.toLowerCase());
      if (hasAdmin) {
        return {
          ...current,
          users: current.users.map((account) =>
            account.login.toLowerCase() === ADMIN_LOGIN.toLowerCase()
              ? {
                  ...account,
                  login: ADMIN_LOGIN,
                  name: ADMIN_LOGIN,
                  role: "administrator",
                  password: account.password || ADMIN_PASSWORD,
                  status: "aktywny"
                }
              : account
          )
        };
      }

      return {
        ...current,
        users: [
          {
            id: "usr-admin",
            login: ADMIN_LOGIN,
            name: ADMIN_LOGIN,
            role: "administrator",
            password: ADMIN_PASSWORD,
            status: "aktywny",
            createdAt: "2026-05-16 00:00"
          },
          ...current.users
        ]
      };
    });
  }, []);

  function handleLogin(login: string, password: string, _remember: boolean, targetView?: NavKey, adminOnly = false) {
    const sessionUser = authenticateUser(appState.users, login, password);

    if (!sessionUser) {
      return "Nieprawidłowy login albo hasło.";
    }

    if (adminOnly && sessionUser.role !== "administrator") {
      return "Panel SNG Admin jest dostępny tylko dla administratora.";
    }

    const lastLogin = new Date().toLocaleString("pl-PL");
    setUser(sessionUser);
    setInitialView(targetView ?? "pulpit");
    setAppState((current) => ({
      ...current,
      users: current.users.map((account) =>
        account.id === sessionUser.id ? { ...account, lastLogin } : account
      )
    }));

    return null;
  }

  function handleRoleChange(userId: string, role: Role) {
    setAppState((current) => ({
      ...current,
      users: current.users.map((account) => (account.id === userId ? { ...account, role } : account))
    }));

    if (user?.id === userId) {
      setUser({ ...user, role });
    }
  }

  function handleRegister(login: string, password: string, name: string) {
    const normalizedLogin = login.trim().toLowerCase();
    const displayName = name.trim() || login.trim();

    if (!login.trim() || !password.trim()) {
      return "Podaj login i hasło.";
    }

    if (password.trim().length < 4) {
      return "Hasło musi mieć co najmniej 4 znaki.";
    }

    if (appState.users.some((account) => account.login.trim().toLowerCase() === normalizedLogin)) {
      return "Taki login już istnieje.";
    }

    const userId = createId("usr");
    const stamp = nowStamp();
    setAppState((current) => ({
      ...current,
      users: [
        ...current.users,
        {
          id: userId,
          login: login.trim(),
          name: displayName,
          role: "podglad",
          password: password.trim(),
          status: "zablokowany",
          createdAt: stamp
        }
      ],
      requests: [
        {
          id: createId("REQ"),
          type: "REG",
          status: "oczekuje",
          priority: "normalny",
          sender: login.trim(),
          target: userId,
          subject: `Rejestracja konta: ${login.trim()}`,
          description: `Nowe konto oczekuje na akceptację administratora. Nazwa: ${displayName}.`,
          createdAt: stamp
        },
        ...current.requests
      ],
      archive: [
        {
          id: createId("arc"),
          category: "Rejestracja",
          title: `Zgłoszono konto ${login.trim()}`,
          owner: login.trim(),
          archivedAt: stamp
        },
        ...current.archive
      ]
    }));

    return null;
  }

  function handleChangePassword(login: string, oldPassword: string, newPassword: string) {
    if (!login.trim() || !oldPassword || !newPassword) {
      return "Wypełnij login, stare hasło i nowe hasło.";
    }

    if (newPassword.length < 4) {
      return "Nowe hasło musi mieć co najmniej 4 znaki.";
    }

    const normalizedLogin = login.trim().toLowerCase();
    const account = appState.users.find((candidate) => candidate.login.trim().toLowerCase() === normalizedLogin);

    if (!account || account.password !== oldPassword) {
      return "Nieprawidłowy login albo stare hasło.";
    }

    const stamp = nowStamp();
    setAppState((current) => ({
      ...current,
      users: current.users.map((candidate) =>
        candidate.id === account.id ? { ...candidate, password: newPassword } : candidate
      ),
      archive: [
        {
          id: createId("arc"),
          category: "Hasło",
          title: `Zmiana hasła użytkownika ${account.login}`,
          owner: account.login,
          archivedAt: stamp
        },
        ...current.archive
      ]
    }));

    return null;
  }

  function handlePasswordResetRequest(login: string) {
    const normalizedLogin = login.trim().toLowerCase();
    const account = appState.users.find((candidate) => candidate.login.trim().toLowerCase() === normalizedLogin);

    if (!account) {
      return "Nie znaleziono takiego loginu.";
    }

    const stamp = nowStamp();
    setAppState((current) => ({
      ...current,
      requests: [
        {
          id: createId("REQ"),
          type: "RESET",
          status: "oczekuje",
          priority: "normalny",
          sender: account.login,
          target: account.id,
          subject: `Reset hasła: ${account.login}`,
          description: "Użytkownik poprosił administratora o nadanie nowego hasła.",
          createdAt: stamp
        },
        ...current.requests
      ]
    }));

    return null;
  }

  function handleAppStateChange(updater: (current: AppState) => AppState) {
    setAppState(updater);
  }

  function handleResetLocalState() {
    window.localStorage.removeItem(STORAGE_KEY);
    if (supabase) {
      window.location.reload();
      return;
    }

    const freshState = createInitialAppState();
    setAppState(freshState);
    setUser({
      id: "usr-admin",
      login: ADMIN_LOGIN,
      name: ADMIN_LOGIN,
      role: "administrator"
    });
  }

  useEffect(() => {
    if (!introVisible) {
      return;
    }

    const timer = window.setTimeout(() => setIntroVisible(false), 4700);
    return () => window.clearTimeout(timer);
  }, [introVisible]);

  if (params.has("debugCloud")) {
    return (
      <main className="debug-cloud">
        <h1>Diagnostyka chmury SNG</h1>
        <dl>
          <div>
            <dt>VITE_SUPABASE_URL</dt>
            <dd>{cloudDiagnostics.hasUrl ? cloudDiagnostics.url : "BRAK W BUILDZIE"}</dd>
          </div>
          <div>
            <dt>VITE_SUPABASE_ANON_KEY</dt>
            <dd>{cloudDiagnostics.hasAnonKey ? cloudDiagnostics.anonKeyPrefix : "BRAK W BUILDZIE"}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{supabase ? (cloudReady ? "Chmura polaczona" : "Chmura skonfigurowana, ale jeszcze nie polaczona") : "Tryb lokalny - Cloudflare nie wbudowal zmiennych"}</dd>
          </div>
        </dl>
      </main>
    );
  }

  if (cloudLoading) {
    return (
      <main className="debug-cloud">
        <h1>Ładowanie danych SNG</h1>
        <p>Pobieram aktualny stan z chmury, żeby każda przeglądarka widziała te same dane.</p>
      </main>
    );
  }

  if (introVisible) {
    return <Intro onDone={() => setIntroVisible(false)} />;
  }

  if (user) {
    return (
      <Dashboard
        user={user}
        appState={appState}
        onLogout={() => setUser(null)}
        onRoleChange={handleRoleChange}
        onAppStateChange={handleAppStateChange}
        onResetLocalState={handleResetLocalState}
        initialView={initialView}
        cloudOnline={Boolean(supabase && cloudReady)}
      />
    );
  }

  return (
    <LoginScreen
      onLogin={handleLogin}
      onRegister={handleRegister}
      onChangePassword={handleChangePassword}
      onPasswordResetRequest={handlePasswordResetRequest}
    />
  );
}
